import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isTelegram, getTelegramUser, expandApp, DEV_TELEGRAM_ID } from '../../utils/telegram';
import api from '../../utils/api';

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCities = async () => {
    setCitiesLoading(true);
    setError('');
    try {
      const response = await api.get('/api/app/registration-options');
      setAvailableCities(response.data.cities || []);
    } catch {
      setError('Не удалось загрузить список городов');
    } finally {
      setCitiesLoading(false);
    }
  };

  useEffect(() => { expandApp(); loadCities(); }, []);

  const toggleCity = (city) => {
    setCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);
  };

  const getTelegramId = () => {
    const tgUser = getTelegramUser();
    if (tgUser) return tgUser.id;
    if (process.env.REACT_APP_DEV_MODE === 'true') return parseInt(DEV_TELEGRAM_ID);
    return null;
  };

  const getTelegramUsername = () => {
    const tgUser = getTelegramUser();
    return tgUser?.username || '';
  };

  const handleSubmit = async () => {
    const telegramId = getTelegramId();
    if (!telegramId) { setError('Не удалось получить Telegram ID'); return; }
    if (!firstName.trim() || !lastName.trim()) { setError('Введите имя и фамилию'); return; }
    if (cities.length === 0) { setError('Выберите хотя бы один город'); return; }

    setLoading(true);
    setError('');
    try {
      await api.post('/api/app/register', {
        telegram_id: telegramId,
        telegram_username: getTelegramUsername(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        self_description: description.trim(),
        cities_requested: cities,
      });
      navigate('/app/waiting');
    } catch (err) {
      if (err.response?.status === 409) navigate('/app');
      else setError(err.response?.data?.detail || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-page">
      <div className="app-header">
        <h1>Регистрация</h1>
        <div className="step-dots">
          {[1, 2, 3].map(s => <div key={s} className={`step-dot ${step >= s ? 'active' : ''}`} />)}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {step === 1 && (
        <div className="app-section">
          <h2>Выбери город</h2>
          <p className="hint">Можно выбрать несколько</p>
          <div className="chips-group">
            {citiesLoading && <div className="hint">Загрузка городов...</div>}
            {!citiesLoading && availableCities.map(city => (
              <button
                key={city}
                className={`chip ${cities.includes(city) ? 'chip--active' : ''}`}
                onClick={() => toggleCity(city)}
              >
                {city}
              </button>
            ))}
          </div>
          {!citiesLoading && availableCities.length === 0 && <button className="btn-secondary full-width" onClick={loadCities}>Повторить</button>}
          <button className="btn-primary full-width mt-auto" disabled={citiesLoading || cities.length === 0} onClick={() => setStep(2)}>
            Далее →
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="app-section">
          <h2>Имя и фамилия</h2>
          <div className="form-group">
            <label>Имя *</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Иван" maxLength={100} />
          </div>
          <div className="form-group">
            <label>Фамилия *</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Петров" maxLength={100} />
          </div>
          <div className="step-nav">
            <button className="btn-ghost" onClick={() => setStep(1)}>← Назад</button>
            <button className="btn-primary" disabled={!firstName.trim() || !lastName.trim()} onClick={() => setStep(3)}>Далее →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="app-section">
          <h2>Должность и место</h2>
          <p className="hint">Напишите в свободной форме, чтобы администратор мог назначить вам нужные права</p>
          <div className="form-group">
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Например: продавец в ТРК Континент, менеджер оптового отдела, офис..."
              rows={4}
              maxLength={500}
            />
            <div className="char-count">{description.length}/500</div>
          </div>
          <div className="step-nav">
            <button className="btn-ghost" onClick={() => setStep(2)}>← Назад</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Отправка...' : '✅ Отправить заявку'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
